/**
 * Drawing Adapter
 *
 * Converts between ResolvedDrawing (block system) and Drawing (canvas).
 * Handles the complex nested structure with control points, bezier handles, and path segments.
 *
 * Path Segments Migration:
 * - Path segments are now stored as references in block_references table
 * - reference_type = 'path_connection'
 * - source_block_id = start control point
 * - target_block_id = end control point
 * - display_options = { segmentType: 'line'|'quadratic'|'cubic', drawingId: string }
 */

import type {
	ResolvedDrawing,
	ControlPointBlock,
	PreSnapMarkerBlock,
	BlockReference,
} from '@/types/blocks'
import type { ControlPointType, PathSegmentType } from '@/types/blocks/Enums'
import type { Drawing, ControlPoint, PathSegment, PathStyle } from '@/types/Drawing.types'
import {
	ControlPointPropertiesSchema,
	DrawingPropertiesSchema,
	PreSnapMarkerPropertiesSchema,
} from '@/types/BlockProperties'
import { extractPointFromProperties } from '@/utils/Block.utils'

/**
 * Convert a ControlPointBlock to a canvas ControlPoint.
 *
 * Uses embedded handles from properties.handles.
 *
 * @param controlPoint - The control point block
 * @returns Canvas ControlPoint object
 * @throws ZodError if properties validation fails
 */
function controlPointBlockToControlPoint(
	controlPoint: ControlPointBlock
): ControlPoint {
	// Validate control point properties before transformation
	const props = ControlPointPropertiesSchema.parse(controlPoint.properties)

	// Spatial properties accessed from properties (Phase 6 migration)
	const pointData = extractPointFromProperties(controlPoint.properties)

	const point: ControlPoint = {
		id: controlPoint.id,
		x: pointData?.x ?? 0,
		y: pointData?.y ?? 0,
		type: props.pointType,
	}

	// Use embedded handles from properties.handles
	if (props.handles) {
		if (props.handles.in) {
			point.handleIn = { x: props.handles.in.x, y: props.handles.in.y }
		}
		if (props.handles.out) {
			point.handleOut = { x: props.handles.out.x, y: props.handles.out.y }
		}
	}

	return point
}

/**
 * Convert a path_connection BlockReference to a canvas PathSegment.
 *
 * Path connections are stored in block_references with:
 * - source_block_id = start control point
 * - target_block_id = end control point
 * - display_options = { segmentType: 'line'|'quadratic'|'cubic', drawingId: string }
 *
 * @param ref - The path connection reference
 * @returns Canvas PathSegment object with string point IDs
 */
function pathConnectionToPathSegment(ref: BlockReference): PathSegment {
	const displayOptions = ref.display_options as { segmentType?: 'line' | 'quadratic' | 'cubic' }

	return {
		type: displayOptions.segmentType ?? 'line',
		pointIds: [ref.source_block_id, ref.target_block_id],
	}
}

/**
 * Extract PathStyle from a DrawingBlock.
 *
 * @param drawing - The drawing block
 * @returns PathStyle object for canvas rendering
 * @throws ZodError if properties validation fails
 */
function extractPathStyle(drawing: ResolvedDrawing['drawing']): PathStyle {
	// Validate properties before transformation
	const props = DrawingPropertiesSchema.parse(drawing.properties)

	return {
		color: props.style?.color ?? '#000000',
		strokeWidth: props.style?.strokeWidth ?? 2,
		brushSize: props.style?.brushSize,
		lineStyle: props.style?.lineStyle ?? 'solid',
		lineEnd: props.style?.lineEnd ?? 'none',
		pathMode: props.style?.pathMode ?? 'sharp',
	}
}

/**
 * Convert a PreSnapMarkerBlock to the pre-snap motion structure.
 *
 * @param marker - The pre-snap marker block
 * @returns Pre-snap motion object, or undefined if no marker
 * @throws ZodError if properties validation fails
 */
function preSnapMarkerToMotion(
	marker: PreSnapMarkerBlock | undefined
): Drawing['preSnapMotion'] | undefined {
	if (!marker) return undefined

	// Validate properties before transformation
	const props = PreSnapMarkerPropertiesSchema.parse(marker.properties)

	return {
		type: props.motionType,
		snapPointId: props.snapPointId ? String(props.snapPointId) : undefined,
	}
}

/**
 * Convert a ResolvedDrawing to a canvas Drawing.
 *
 * Path segments are now read from pathConnections (BlockReference[]) instead of
 * the deprecated pathSegments property.
 *
 * @param drawingBlock - The resolved drawing block with all children
 * @returns Canvas Drawing object for rendering
 * @throws ZodError if properties validation fails
 */
export function drawingBlockToDrawing(drawingBlock: ResolvedDrawing): Drawing {
	const { drawing, controlPoints, pathConnections, preSnapMarkers } = drawingBlock

	// Validate drawing properties early
	const drawingProps = DrawingPropertiesSchema.parse(drawing.properties)

	// Build points record from control points
	const points: Record<string, ControlPoint> = {}
	for (const cp of controlPoints) {
		const point = controlPointBlockToControlPoint(cp)
		points[point.id] = point
	}

	// If drawing is linked to a player, override the linked control point's position
	// with the player's current position. This ensures the drawing always visually
	// connects to the player regardless of what's stored in the control point block.
	if (drawingBlock.linkedPlayer && drawingBlock.linkedPointId) {
		const linkedPoint = points[drawingBlock.linkedPointId]
		const playerPos = extractPointFromProperties(drawingBlock.linkedPlayer.properties as Record<string, unknown>)
		if (linkedPoint && playerPos) {
			points[drawingBlock.linkedPointId] = { ...linkedPoint, x: playerPos.x, y: playerPos.y }
		}
	}

	// Convert path connections (from block_references) to segments
	const segments: PathSegment[] = pathConnections.map(pathConnectionToPathSegment)

	// Get first pre-snap marker (typically only one per drawing)
	const preSnapMotion = preSnapMarkerToMotion(preSnapMarkers[0])

	return {
		id: drawing.id,
		points,
		segments,
		style: extractPathStyle(drawing),
		annotations: [], // Annotations are handled separately via block_references
		templateId: drawingProps.templateId ?? undefined,
		preSnapMotion,
		playerId: drawingBlock.linkedPlayer?.id ?? undefined,
		linkedPointId: drawingBlock.linkedPointId ?? undefined,
	}
}

/**
 * Convert an array of ResolvedDrawing to canvas Drawings.
 *
 * @param drawingBlocks - Array of resolved drawing blocks
 * @returns Array of canvas Drawing objects
 */
export function drawingBlocksToDrawings(
	drawingBlocks: ResolvedDrawing[]
): Drawing[] {
	return drawingBlocks.map(drawingBlockToDrawing)
}

// ========== Reverse Adapter: Canvas → Blocks ==========

/**
 * Payload for creating a complete drawing with all child blocks.
 * Used by addDrawing mutation to send full geometry to server.
 */
export interface CreateDrawingPayload {
	/** Style properties for the parent DrawingBlock */
	style: {
		color: string
		strokeWidth: number
		brushSize?: number
		lineStyle: 'solid' | 'dashed'
		lineEnd: 'none' | 'arrow' | 'tShape'
		pathMode: 'sharp' | 'curve'
	}
	/** Control points to create as child blocks */
	controlPoints: Array<{
		/** Original ID from canvas Drawing.points - used for segment mapping */
		canvasId: string
		x: number
		y: number
		pointType: ControlPointType
		handles?: {
			in?: { x: number; y: number }
			out?: { x: number; y: number }
		}
	}>
	/** Path segments to create as block_references with path_connection type */
	pathConnections: Array<{
		/** Canvas ID of source control point */
		sourceCanvasId: string
		/** Canvas ID of target control point */
		targetCanvasId: string
		segmentType: PathSegmentType
	}>
	/** Player link — connects this drawing's start point to a player */
	playerLink?: {
		playerId: string
		linkedPointCanvasId: string
	}
}

/**
 * Convert a canvas Drawing object to a block creation payload.
 *
 * This is the reverse of drawingBlockToDrawing() - it takes a canvas-layer
 * Drawing and converts it to the format needed to create blocks via the API.
 *
 * @param drawing - Canvas Drawing object with points, segments, style
 * @returns Payload for creating DrawingBlock + ControlPointBlocks + path_connection references
 */
export function drawingToCreatePayload(drawing: Drawing): CreateDrawingPayload {
	// Extract style with defaults
	const style = {
		color: drawing.style.color ?? '#000000',
		strokeWidth: drawing.style.strokeWidth ?? 2,
		brushSize: drawing.style.brushSize,
		lineStyle: (drawing.style.lineStyle ?? 'solid') as 'solid' | 'dashed',
		lineEnd: (drawing.style.lineEnd ?? 'arrow') as 'none' | 'arrow' | 'tShape',
		pathMode: (drawing.style.pathMode ?? 'curve') as 'sharp' | 'curve',
	}

	// Convert points record to array with canvas IDs preserved
	const controlPoints = Object.entries(drawing.points).map(([canvasId, point]) => ({
		canvasId,
		x: point.x,
		y: point.y,
		pointType: (point.type ?? 'intermediate') as ControlPointType,
		handles: point.handleIn || point.handleOut
			? {
					in: point.handleIn ? { x: point.handleIn.x, y: point.handleIn.y } : undefined,
					out: point.handleOut ? { x: point.handleOut.x, y: point.handleOut.y } : undefined,
				}
			: undefined,
	}))

	// Convert segments to path connections (path segments have exactly 2 point IDs)
	const pathConnections = drawing.segments
		.filter((segment) => segment.pointIds.length >= 2)
		.map((segment) => ({
			sourceCanvasId: segment.pointIds[0]!,
			targetCanvasId: segment.pointIds[1]!,
			segmentType: (segment.type ?? 'line') as PathSegmentType,
		}))

	// Build player link if drawing has a linked player and linked point
	const playerLink = drawing.playerId && drawing.linkedPointId
		? { playerId: drawing.playerId, linkedPointCanvasId: drawing.linkedPointId }
		: undefined

	return {
		style,
		controlPoints,
		pathConnections,
		playerLink,
	}
}
